/**
 * R6 §7, generalized by R7 WS-0 §5 — the route table, and the ONE place chrome and content agree
 * on what is in scope.
 *
 * Zero legacy URL aliases, with four named exceptions. The old `AppHost` carries ~20 redirects for
 * pre-restructure URLs. This was a greenfield path space when it was written — no URL had ever pointed
 * at it — so nothing to alias. Copying them would create redirect targets that were never sources.
 *
 * The four exceptions are the old Cost paths `/platform/billing/ledger`, `/reconciliation`,
 * `/autoscale` and `/rate-card` (plan 2026-10-12-console-ward-billing, D12). Each was a live rail item
 * with real bookmarks, and the Fleet capacity page links to `/platform/billing/autoscale`. Each alias
 * matches one literal path and redirects to `/platform/billing` with its tab and its query (see
 * `LegacyCostRedirect`).
 *
 * ⛔ Every `to=` and `path=` here is ROUTER-RELATIVE. `<BrowserRouter basename>` prepends the
 * basename to every emitted href and strips it from every match, so a hand-written base prefix
 * inside one doubles. ⚠️ `BASENAME` is `''` today, so that doubling would be invisible until a
 * basename is reintroduced — keep writing router paths regardless.
 */
import { Suspense, lazy, useState, type ComponentType } from 'react';
import { Loader2 } from 'lucide-react';
import { Navigate, Route, Routes, useLocation, useMatch } from 'react-router';
import type { App } from '../api/apps.api';
import { notOfferedLabel, type AvailabilitySummaryState } from '../panes/availability-model';
import { legacyCostTarget, type LegacyCostSegment } from '../panes/billing-cost-model';
import { pendingAccessRequestsLabel } from '../panes/access-requests-model';
import { pendingAccessRequestCount } from './access-request-summary-context';
import type { AccessRequestSummary } from '../api/accessRequests.api';
import type { AsyncState } from '../panes/async-state';
import { AppLoader } from './AppLoader';
import { AppScopeProvider } from './app-scope-context';
import { isPathUnder } from './path-under';
import { useAppRegistry } from './AppRegistryContext';
import { useGrantsReread, useOptionalGrantsState, type GrantsReread } from './grants-context';
import { useOptionalAppsContext, type AppsState } from './apps-context';
import type { GrantsState } from './grants-state';
import { DetailScopeNameProvider, useDetailScopeName } from './detail-scope-context';
import { useDataPlaneChoices } from './data-plane-choices-context';
import {
  DATA_PLANES_ROUTE,
  DP_TAB_DEFAULT,
  dataPlaneRoute,
  parseDpTabSegment,
  type DpTab,
} from '../panes/dp-tabs';
import type { DataPlaneSummary } from '../api/dataPlanes.api';
import { landingPath, type Landing } from './landing';
import {
  createShellEntry,
  detailShellEntry,
  onRail,
  type AppScopedEntry,
  type DetailScope,
  type GlobalEntry,
  type ShellSection,
} from './manifest';
import { NotAuthorizedPanel } from './NotAuthorizedPanel';
import { Button } from './Button';
import { PaneFrame } from './PaneFrame';
import { SectionLayout } from './SectionLayout';
import type { ShellSession } from './SectionHeader';
import type { RailItem, RailScope, RailScopePicker } from './NavRail';
import type { GrantsError } from './ZeroAccessPanel';

/*
 * ⛔ **`APP_TAB_PANEL_ID` AND THE `role="tabpanel"` WRAPPER ARE DELETED** (R9 N-8). They existed for
 * `AppTabStrip`'s `aria-controls`, and **a tabpanel with no tablist is invalid ARIA** — a state this
 * file previously went out of its way to make unreachable. With the strip gone there is no tablist in
 * app scope at all, so the wrapper has nothing to be controlled by.
 *
 * ⚠️ **The HEIGHT BOUND it also happened to provide is not lost, and that is the part worth checking
 * before deleting a wrapper.** `flex-1 min-h-0` on that div was load-bearing for the app-scoped
 * routes: without a definite height, `PaneFrame`'s `h-full` resolved against a content-driven box,
 * overflowed `<main>`, and the pane's chrome scrolled away (measured: five app-scoped routes with
 * `<main>` scrolling 213–871px and `region.scrollTop` at 0). ⇒ With the strip gone, `<main>`'s only
 * child is `PaneFrame` again, whose `h-full` resolves against `<main>` directly — the same shape every
 * non-app route has always had, and the shape the `strip === null` branch already used.
 */

/**
 * The redirect of one old Cost path (D12). It renders `<Navigate replace>` to the target of
 * `legacyCostTarget(segment, location.search)`: the constant `/platform/billing`, the mapped tab and
 * the old query, re-serialized. The hash drops, and Back does not return to the old path.
 */
function LegacyCostRedirect({ segment }: { readonly segment: LegacyCostSegment }): React.JSX.Element {
  const target = legacyCostTarget(segment, useLocation().search);
  return <Navigate to={target} replace />;
}

/** `/apps/:appId/<subPath>`, with the empty `subPath` collapsing to the app root. */
function appScopedPath(appId: string, subPath: string): string {
  return subPath === '' ? `/apps/${appId}` : `/apps/${appId}/${subPath}`;
}

/**
 * `${routePrefix}/${recordId}/<subPath>`, with the empty `subPath` collapsing to the record's own
 * URL — the detail-scope twin of {@link appScopedPath}.
 *
 * ⛔ **BOTH THE ROUTE AND THE RAIL LINK COME THROUGH HERE**, which is what makes it impossible for a
 * rail item to point somewhere the router does not serve. `AppHost.tsx`'s header has held that rule
 * since the `SectionRail` module doc (*"the rail constructs no routes"*) and it is the rule that keeps the
 * `<Route path="*">` teleport unreachable: a link the rail cannot mis-build is a link that cannot
 * fall through to the landing screen.
 */
export function detailScopedPath(routePrefix: string, recordId: string, subPath: string): string {
  const base = `${routePrefix}/${recordId}`;
  return subPath === '' ? base : `${base}/${subPath}`;
}

/** Where the caller is, inside a detail scope. @see resolveDetailScope */
export interface DetailScopeMatch {
  readonly entry: GlobalEntry;
  readonly scope: DetailScope;
  /** The `:paramName` segment — a DP's UUID. */
  readonly recordId: string;
}

/**
 * ⭐ **WHICH DETAIL SCOPE THE PATH IS IN, AS ONE PURE ANSWER** (R9-11/12).
 *
 * ⛔ **A PURE FUNCTION OVER `pathname`, NOT A `useMatch` PER ENTRY, AND THAT IS NOT A STYLE CHOICE.**
 * The obvious implementation is `entries.map(e => useMatch(...))` — a hook inside a loop over an
 * array that comes from context and IS overridable per test (`AppRegistryProvider`'s
 * `globalEntries` prop). A registry of a different length between two renders is a change in hook
 * COUNT, which React treats as a corrupted component. Exported and pure means both arms are also
 * asserted directly instead of inferred from a rendered tree — the same reason `groupRailBySection`
 * and `railAriaCurrent` are pure.
 *
 * ⛔ **THE CREATE SEGMENT IS EXCLUDED, AND IT IS A REAL DEFECT RATHER THAN A HYPOTHETICAL.**
 * `/platform/data-planes/new` is the create screen (`entry.create.segment`), and it is
 * indistinguishable from a record id by shape. Without this guard the New-data-plane form renders
 * with a scoped rail group whose six links all point at `/platform/data-planes/new/<dest>` — six
 * dead links and a chip naming a record that does not exist yet, on the one screen where nothing has
 * an id. ⚠️ The detail ROUTE does not have this problem (react-router ranks the static `new` above
 * the dynamic `:dpId`), so the guard is needed **here and only here** — which is exactly the kind of
 * asymmetry that ships.
 *
 * ⚠️ Segment-boundary matching, like `resolveActiveSection`: a bare `startsWith` would make
 * `/platform/data-planes-archive/x` resolve into the data-planes scope.
 */
export function resolveDetailScope(
  pathname: string,
  entries: readonly GlobalEntry[],
): DetailScopeMatch | null {
  for (const entry of entries) {
    const scope = entry.detail?.scoped;
    if (scope === undefined) continue;
    const prefix = `${entry.routePrefix}/`;
    if (!pathname.startsWith(prefix)) continue;
    /*
     * ⛔ **`indexOf` + `slice`, NOT `.split('/')[0] ?? ''` — and the difference is a COVERAGE fact,
     * not a style one.** `String.prototype.split` always returns at least one element, so under
     * `noUncheckedIndexedAccess` the compiler demands a `?? ''` whose arm **can never execute**. That
     * is an unreachable branch inside a function this package requires to be 100% covered, and the
     * only ways to "fix" it are to suppress the check or to write a test that cannot fail — both of
     * which this repo forbids outright. ⇒ the construct with no phantom arm is the correct answer.
     *
     * ⚠️ Both arms here are real and both are exercised: `-1` is the record root
     * (`/platform/data-planes/:id`), anything else is a deeper destination.
     */
    const rest = pathname.slice(prefix.length);
    const slash = rest.indexOf('/');
    const recordId = slash === -1 ? rest : rest.slice(0, slash);
    // `''` is `${routePrefix}/` with a trailing slash and nothing after it — not a record.
    if (recordId === '') continue;
    // Lower case, because the router matches the static create segment with no regard to case.
    if (recordId.toLowerCase() === entry.create?.segment.toLowerCase()) continue;
    return { entry, scope, recordId };
  }
  return null;
}

/**
 * Below this many reachable apps the scope chip is a **label, not a control** (§4.2).
 *
 * ⛔ **DELIBERATELY ITS OWN CONSTANT AND NOT `MIN_TABS` FROM `./tab-keyboard`**, even though both are
 * `2` and both encode *"a control offering one choice is worse than a label"*. They govern different
 * controls with different reasons to change, and `02-NAV-AND-PORT.md` §4.3 flags the confusion as *"one
 * edit away"* now that the two live in the same tree: `MIN_TABS` must NEVER be applied to rail groups,
 * where a group of one is a real destination. Sharing the symbol is how that edit gets made.
 */
const MIN_APPS_FOR_PICKER = 2;

/** Below this many data planes the data plane chip is a label (plan 2026-10-14-console-dp-picker, D3). */
const MIN_DATA_PLANES_FOR_PICKER = 2;

/**
 * The data plane tab the operator is on, read from the first path segment after the record id
 * (plan 2026-10-14-console-dp-picker, D7). Deeper segments do not count, and a segment that names
 * no tab gives the default tab. The picker rows keep this tab on a switch.
 */
function currentDataPlaneTab(pathname: string, match: DetailScopeMatch): DpTab {
  const rest = pathname.slice(
    detailScopedPath(match.entry.routePrefix, match.recordId, '').length + 1,
  );
  const slash = rest.indexOf('/');
  return parseDpTabSegment(slash === -1 ? rest : rest.slice(0, slash)) ?? DP_TAB_DEFAULT;
}

/**
 * The data plane picker over a list of 2 or more rows (D1, D4, D8, D9). The filter field
 * (`searchable`) is the search box that section 5 of that plan revived on 2026-10-20.
 *
 * Each row keeps the current tab, and `dataPlaneRoute` builds it from the server id. `current` is
 * the list row of the current data plane. When the list holds no such row, the picker marks no row.
 */
function buildDataPlanePicker(
  dataPlanes: readonly DataPlaneSummary[],
  current: DataPlaneSummary | undefined,
  match: DetailScopeMatch,
  pathname: string,
): RailScopePicker {
  const tab = currentDataPlaneTab(pathname, match);
  return {
    choices: dataPlanes.map((dp) => ({
      id: dp.id,
      key: dp.id,
      name: dp.name,
      subject: dp.region,
      to: dataPlaneRoute(dp.id, tab),
    })),
    currentId: current?.id ?? match.recordId,
    // The region goes under the name, so a long region does not take the name's width (D4).
    rowLayout: 'stacked',
    allTo: DATA_PLANES_ROUTE,
    allLabel: 'All data planes',
    // The filter matches the name and the region. The id stays out: one hex letter matches most ids.
    searchable: true,
  };
}

/**
 * Which section the current path is in. ⛔ SEGMENT-BOUNDARY matching, longest prefix first.
 *
 * A bare `startsWith(prefix)` makes `/platform/tenants` match `/platform/tenants-archive`, and
 * longest-first is what makes `/platform/tenants` beat a hypothetical `/platform`. Both are the
 * classic prefix-router bugs and both are pinned by `active-section.test.ts`.
 *
 * ## ⚠️⚠️ R9 **Q7**: THIS FUNCTION HAS **ZERO PRODUCTION CONSUMERS** AS OF THE NAV RESET — MEASURED
 *
 * Its two callers were the top-bar section strip (which section is lit) and the rail's dynamic
 * accessible name (`"<Section> navigation"`). **Both are deleted**: sections are now rail GROUPS, and
 * the rail's global landmark is statically `aria-label="Console"`. The rail no longer filters by
 * section either — it renders every visible global entry that is not `navHidden` — so nothing asks
 * this question any more.
 *
 * ⛔ **IT IS DELIBERATELY NOT DELETED, AND THAT IS THE RULING, NOT AN OVERSIGHT.** Q7 says *"measure
 * the consumer count, do not assume"* and reserves the deletion. Measured 2026-08-07 by grep +
 * typecheck: production consumers **0**, test consumers **2 files** (`active-section.test.ts`, 13
 * assertions, and `entry-create.test.tsx`, which uses it to pin that a `…/new` create path resolves
 * through its PARENT section). ⇒ reported to the lead rather than removed. ⚠️ A pure, fully-covered,
 * unreferenced export costs nothing; deleting a correct longest-prefix segment matcher and then
 * needing one again is how the two classic prefix bugs come back.
 */
export function resolveActiveSection(
  pathname: string,
  visibleGlobal: readonly GlobalEntry[],
  visibleAppScoped: readonly AppScopedEntry[],
): ShellSection | null {
  let best: { readonly section: ShellSection; readonly len: number } | null = null;
  for (const e of visibleGlobal) {
    const p = e.routePrefix;
    if (isPathUnder(pathname, p)) {
      if (best === null || p.length > best.len) best = { section: e.section, len: p.length };
    }
  }
  if (best !== null) return best.section;
  // An app-scoped path when the catalog entry itself is invisible (the runtime-read-only
  // principal). Without this arm the header shows no active section on the only screen they have.
  if (visibleAppScoped.length > 0 && isPathUnder(pathname, '/apps')) {
    return 'apps';
  }
  return null;
}

/**
 * Whether the app named in the URL is one the caller can reach — as ONE discriminated answer.
 *
 * ⛔ THREE STATES, NEVER TWO FLAGS (lead ruling ACK-ws0-1 / G3). Once Platform stops waiting on
 * `GET /v1/apps`, an app-scoped URL is reachable while the list is still unknown. Passing
 * `apps ?? []` down would make `not-yours` — a DENIAL — the answer for a list nobody has received
 * yet: the `absent → denied` collapse, and the same `null → []` inversion the `apps-context` module doc names
 * forbids, one level lower. An `App | null` plus a boolean would encode three states in four
 * combinations, and someone would eventually construct the meaningless fourth.
 */
export type ScopedAppResolution =
  | { readonly kind: 'unknown' } // the list has not arrived — NOT a denial
  | { readonly kind: 'found'; readonly app: App }
  | { readonly kind: 'not-yours' }; // the list is loaded and this id is not in it

/**
 * `absentDecides` is `false` while `apps` is a kept list that can lack an app the server just made
 * (a re-read in flight or failed). An id in the list is then still `found`, and an absent id is
 * `unknown`, never `not-yours`.
 */
export function resolveScopedApp(
  scopedAppId: string | null,
  apps: readonly App[] | null,
  absentDecides = true,
): ScopedAppResolution {
  // No id in the URL at all: the component was rendered outside its own route. The safe answer is
  // the denial, never an invented id.
  if (scopedAppId === null) return { kind: 'not-yours' };
  if (apps === null) return { kind: 'unknown' };
  const app = apps.find((a) => a.id === scopedAppId);
  if (app !== undefined) return { kind: 'found', app };
  return absentDecides ? { kind: 'not-yours' } : { kind: 'unknown' };
}

/**
 * The `GLOBAL_ENTRIES` id the availability count hangs off (#2621).
 *
 * ⛔ A copy of `manifest.ts`'s `id: 'app-catalog'`, and the copy is held by `W4-05` rather than by
 * an import: the entry objects reach this file already permission-filtered through
 * `useAppRegistry()`, so importing the manifest here to read one id would give the route table a
 * second source for a set it is handed.
 */
const CATALOG_ENTRY_ID = 'app-catalog';

/**
 * The `GLOBAL_ENTRIES` id the pending access-request count hangs off (#2496, D25).
 *
 * ⛔ A copy of `manifest.ts`'s `id: 'access-members'`, held by a test rather than by an import, for
 * the reason {@link CATALOG_ENTRY_ID} states: the entry objects reach this file already
 * permission-filtered through `useAppRegistry()`, so importing the manifest here to read one id
 * would give the route table a second source for a set it is handed. A wrong id here renders no
 * count at all, which is what the end-to-end row in `availability-summary.test.tsx` catches.
 *
 * The count sits on Members, the parent of `access-people` (access-members design, I3). The rail shows
 * a child only while its parent is open, so a count on People would hide on every other page.
 */
const MEMBERS_ENTRY_ID = 'access-members';

export interface AppHostProps {
  readonly landing: Landing;
  /**
   * ⛔ NULLABLE, and it must stay nullable. `null` = the list is not known yet, which is a
   * different answer from `[]` = the caller can reach no apps. See `ScopedAppResolution`.
   */
  readonly apps: readonly App[] | null;
  /**
   * Whether an id absent from `apps` is a denial. `false` while `apps` is the kept list of a
   * re-read in flight or failed (`AuthenticatedShell`), so the absent id waits or names the failed
   * read. Defaults to `true`, a settled list, for a tree that runs no re-read.
   */
  readonly appsAbsentDecides?: boolean;
  readonly grantsError: GrantsError | null;
  readonly session: ShellSession | null;
  /**
   * The fleet-wide availability summary state, carried down from `CpAdminApp` through
   * `AuthenticatedShell` (#2621, R-018, R-041).
   *
   * ⛔ **A PROP, NOT A CONTEXT READ, AND THAT IS THIS FILE'S OWN HEADER RULE.** `AppHost` is the
   * route table; it stays the component that reads nothing from the auth graph. The value arrives
   * already derived, and the one thing this file does with it is set `RailItem.count`.
   *
   * ⛔ **THE RULE THE `railItems` PRODUCER APPLIES, AND NOWHERE ELSE**: for the entry whose `id`
   * is `CATALOG_ENTRY_ID`, and only when the state is `ready` with `summary.notOffered > 0`, it
   * sets `count: { value: notOffered, srLabel: notOfferedLabel(notOffered) }`. Every other entry
   * and every other state leaves `count` absent, so the four non-`ready` arms and the `ready` zero
   * produce the same rail. ⚠️ Moving that rule into `RailLink` would make the rail decide what a
   * count means; moving it into a pane would give one aggregate two readers.
   */
  readonly availabilitySummary: AvailabilitySummaryState;
  /**
   * The pending access-request count's state, carried down from `CpAdminApp` through
   * `AuthenticatedShell` (#2496, D25).
   *
   * ⛔ **A PROP, NOT A CONTEXT READ, AND THAT IS THIS FILE'S OWN HEADER RULE** — the same rule
   * {@link AppHostProps.availabilitySummary} states. `AppHost` is the route table; it reads nothing
   * from the auth graph. The value arrives already derived (the `cp:operator:read` pre-check was
   * turned into an arm by `visibleAccessRequestSummary` in `CpAdminApp`), and the one thing this
   * file does with it is set `RailItem.count` on the `access-members` entry.
   *
   * ⛔ **THE RULE IS {@link pendingAccessRequestCount}'s, NOT THIS FILE'S**: a count only from a
   * `ready` non-zero, so in-flight, failed, forbidden and a collected zero all render the same
   * rail. ⚠️ Moving that rule into `RailLink` would make the rail decide what a count means; moving
   * it into `PeoplePane` would give one aggregate two readers.
   */
  readonly accessRequestSummary: AsyncState<AccessRequestSummary>;
}

/**
 * ⛔ **A THIN WRAPPER WHOSE ONLY JOB IS TO BE *ABOVE* {@link AppHostFrame}** — because a component
 * cannot read a context it renders the provider for, and the rail (chrome, in the frame) has to read
 * a name the pane (content, under the frame) writes. @see detail-scope-context
 *
 * ⚠️ The split is the smallest possible: everything real stays in the frame, which is also what
 * every existing test renders.
 */
export function AppHost(props: AppHostProps): React.JSX.Element {
  return (
    <DetailScopeNameProvider>
      <AppHostFrame {...props} />
    </DetailScopeNameProvider>
  );
}

export function AppHostFrame({
  landing,
  apps,
  appsAbsentDecides = true,
  grantsError,
  session,
  availabilitySummary,
  accessRequestSummary,
}: AppHostProps): React.JSX.Element {
  const { globalEntries, visibleGlobal, visibleAppScoped } = useAppRegistry();
  const { pathname } = useLocation();

  /*
   * ⛔ ONE resolution of "which app is in scope", read here and shared by the chrome AND the
   * content. Resolving it separately in the rail, the scope chip and the pane is three
   * implementations of the same question — the exact drift `landing.ts` exists to prevent one
   * level up. `useMatch` works because this component is already inside `<BrowserRouter>`, and
   * unlike `useParams` it is usable OUTSIDE a `<Route>` element, which is what lets the chrome
   * (rendered outside `<Routes>`) see the same answer the content does.
   */
  const scopeMatch = useMatch('/apps/:appId/*');
  const scopedAppId = scopeMatch?.params.appId ?? null;
  const scoped = resolveScopedApp(scopedAppId, apps, appsAbsentDecides);
  const scopedApp = scoped.kind === 'found' ? scoped.app : null;

  /*
   * ⛔ **THE ONE PLACE A SUMMARY STATE BECOMES A COUNT** (#2621, R-021). `null` for every arm that
   * is not a `ready` non-zero, so the four absent arms and the collected zero produce the same
   * rail. A `ready` zero is deliberately in the same bucket: the tile states it, and a rail count
   * exists to draw the eye to something.
   *
   * ⚠️ The state arrives already derived — `visibleSummaryState` in `CpAdminApp` turned the
   * pre-checks into arms — so nothing here reads a permission. This file is the route table.
   */
  const notOfferedCount =
    availabilitySummary.kind === 'ready' && availabilitySummary.summary.notOffered > 0
      ? availabilitySummary.summary.notOffered
      : null;

  /*
   * ⛔ THE SECOND COUNT, ON THE SAME RULE (#2496, D25). `pendingAccessRequestCount` is the arm
   * chooser and it lives beside its own context, so the five states it collapses have a unit test
   * rather than only an end-to-end row. ⚠️ Spelled as a call rather than repeated inline, because
   * the two counts' rule is one rule and a second copy is where *"a `ready` zero is not a count"*
   * gets lost for one of them.
   */
  const pendingRequestCount = pendingAccessRequestCount(accessRequestSummary);

  /*
   * ⭐ **THE RAIL IS NOW GLOBAL-SECTIONS-ONLY *AND ALL OF THEM*** (R9 N-2). It used to filter to the
   * ACTIVE section, because the top-bar strip chose the section and the rail showed its contents. With
   * the strip deleted the rail carries every destination the principal can reach — five labelled
   * groups — and `NavRail` groups them by `SECTION_ORDER`.
   *
   * ⚠️ `resolveActiveSection` is therefore no longer consulted here. See its docblock for the Q7
   * measurement; it is retained, unreferenced in production, by ruling.
   *
   * ⛔ **THIS STAYS THE ONE PLACE A RAIL PATH IS CONSTRUCTED** (the rule the `SectionRail` module doc set and
   * `NavRail` keeps): `to` arrives at the rail already resolved, so `/apps/:appId/<subPath>` is built
   * once, here, from the registry's permission-filtered set.
   */
  const railItems: readonly RailItem[] = visibleGlobal.filter(onRail).map((e) => ({
    id: e.id,
    to: e.routePrefix,
    displayName: e.displayName,
    iconKey: e.iconKey,
    section: e.section,
    /*
     * ⛔ The catalogue entry alone. `CATALOG_ENTRY_ID` is a copy of `manifest.ts`'s `id`, and
     * `W4-05` is its pin end to end: it reads the count off `nav-app-catalog` and counts the
     * count nodes across the whole rail, so a wrong id here renders no count at all and reds.
     * ⚠️ A conditional SPREAD rather than `count: … ?? undefined`. `exactOptionalPropertyTypes`
     * is off in `tsconfig.base.json`, so both compile — but an explicit `undefined` is still an
     * OWN property, which `Object.keys` and `toStrictEqual` both see. Every other rail item keeps
     * the exact shape it has today.
     */
    ...(e.id === CATALOG_ENTRY_ID && notOfferedCount !== null
      ? { count: { value: notOfferedCount, srLabel: notOfferedLabel(notOfferedCount) } }
      : {}),
    /*
     * ⛔ THE MEMBERS ENTRY ALONE (#2496, D25), as a SECOND conditional spread rather than a branch
     * folded into the one above. The two counts are different facts from different reads, and one
     * combined expression would make them share an arm — so the day a third count arrives, a
     * reader has to unpick which `null` belonged to which. ⚠️ Later in the object literal than the
     * catalogue arm on purpose: no entry can be both ids, so the two never collide, and putting
     * them in declaration order keeps the diff for a third count a single added spread.
     */
    ...(e.id === MEMBERS_ENTRY_ID && pendingRequestCount !== null
      ? {
          count: {
            value: pendingRequestCount,
            srLabel: pendingAccessRequestsLabel(pendingRequestCount),
            // Pending requests wait on an admin, so this count takes the attention tone.
            tone: 'attention',
          },
        }
      : {}),
    /*
     * ⛔ `end` iff the prefix is a SINGLE SEGMENT. `/apps` needs it (deeper siblings exist);
     * `/platform/tenants` must NOT have it, because that is exactly what keeps the parent entry LIT
     * on `/platform/tenants/:tenantId` (R7-DEC-4).
     *
     * ⚠️ R6 already computed `end` this way and parent-stays-lit fell out of it BY COINCIDENCE. It is
     * now a stated contract with two tests, because a later "tidy-up" to `end: true` for consistency
     * would silently unlight every detail screen's parent — a change nothing else in the suite would
     * notice.
     */
    end: e.routePrefix.split('/').filter(Boolean).length === 1,
    /*
     * The rail parent, copied only for an entry that declares one (`GlobalEntry.navParent`). A
     * conditional spread, for the reason the count arms give: every other item keeps its shape.
     */
    ...(e.navParent !== undefined ? { parentId: e.navParent } : {}),
  }));

  /*
   * ⭐ **THE APP-SCOPED SET — THE SCOPED RAIL GROUP** (N-5, ruled: a DISTINCT SIBLING REGION).
   *
   * ⛔ THIS IS THE SAME DERIVATION THE DELETED `AppTabStrip` CONSUMED, unchanged — not a second one.
   * Re-deriving "which screens does this operator have" inside the rail would be the drift this file's
   * own header warns about. A screen added to `APP_SCOPED_ENTRIES` appears here with no edit.
   *
   * ⛔ **PERMISSION-GATED SCREENS ARE ABSENT, NOT DIMMED.** `visibleAppScoped` is already filtered by
   * `entryAllows`, so there is no `disabled` state to add and none should be added — a dimmed rail item
   * implies the screen is one permission away and invites a support ticket for a door that was never
   * theirs.
   *
   * ⚠️ `section` is `'apps'` on every one of them and is NEVER read by the scoped group: `NavRail`
   * takes the scoped set as its own prop precisely so it cannot be folded into the Apps group, which
   * is the nesting N-5 refused.
   *
   * A `navHidden` entry gets no rail item and keeps its route: `AppScopeRoute` reads the unfiltered
   * `visibleAppScoped` (plan 2026-10-13-console-ward-app-drill-in, A-4).
   */
  const appScopedItems: readonly RailItem[] =
    scopedApp === null
      ? []
      : visibleAppScoped.filter((e) => e.navHidden !== true).map((e) => ({
          id: e.id,
          to: appScopedPath(scopedApp.id, e.subPath),
          displayName: e.displayName,
          iconKey: e.iconKey,
          section: e.section,
          // The app root (`subPath: ''`) is a prefix of every sibling, so it needs `end`.
          end: e.subPath === '',
        }));

  /*
   * ⭐⭐ **THE DETAIL SCOPE — THE SECOND ADOPTER OF THE SCOPED RAIL GROUP** (N-11).
   *
   * ⛔ **RESOLVED AGAINST `visibleGlobal`, NOT `globalEntries`.** A principal without `cp:dp:read`
   * gets `NotAuthorizedPanel` on this very route (the invisible-entry arm below), and a scope chip
   * naming a record beside a 403 panel would be the chrome asserting access the content just
   * refused. ⚠️ Note this is the OPPOSITE choice from the route table twenty lines down, which
   * iterates every DECLARED entry on purpose so an unheld URL 403s rather than silently redirecting.
   * Routes must exist for screens you cannot see; rail groups must not.
   */
  const detailScope = resolveDetailScope(pathname, visibleGlobal);
  /*
   * ⚠️ `null` means *"the pane has not told us the record's name yet"*, and the fallback is the
   * record ID — the same rule `DataPlanePane` already applies to its own breadcrumb and heading
   * (`dp?.name ?? dpId`), *"because a skeleton where a name belongs reads as a broken screen"*.
   */
  const detailScopeName = useDetailScopeName();

  // The list loads only while a data plane page is open, because the read is expensive (D6).
  const dataPlaneScope =
    detailScope !== null && detailScope.entry.routePrefix === DATA_PLANES_ROUTE ? detailScope : null;
  const dataPlanes = useDataPlaneChoices(dataPlaneScope !== null);

  const detailScopedItems: readonly RailItem[] =
    detailScope === null
      ? []
      : detailScope.scope.entries.map((e) => ({
          id: e.id,
          to: detailScopedPath(detailScope.entry.routePrefix, detailScope.recordId, e.subPath),
          displayName: e.displayName,
          iconKey: e.iconKey,
          /*
           * ⚠️ The PARENT entry's section, so a detail scope can never be mistaken for a section of
           * its own. `NavRail` takes the scope as a separate prop and never reads this field for a
           * scoped item — it is here because `RailItem` is one type, and a required field with a
           * meaningless value would be the worse alternative.
           */
          section: detailScope.entry.section,
          // The record root (`subPath: ''`) is a prefix of every sibling, so it needs `end`.
          end: e.subPath === '',
        }));

  /*
   * ⭐ **THE APP PICKER, OR `null` FOR A NON-INTERACTIVE BLOCK** (§4.2).
   *
   * ⛔ `null` AT EXACTLY ONE REACHABLE APP, and it is a ruling this package has made twice already:
   * *"a control that opens a list of one is worse than a label"*. ⚠️ `apps === null` (the list has not
   * arrived) also yields `null` — a chip that becomes interactive a moment later is a control appearing
   * under the operator's cursor, and `absent → denied`'s sibling defect is `absent → available`.
   *
   * ⛔⛔ **LEAD RULING Q4 IS REVERSED (owner, 2026-08-07).** Q4 shipped the ≥2 case as a plain LINK to
   * `/apps` with `aria-haspopup` omitted; the chevron stayed, so the chip signified a menu and delivered
   * a navigation. *"I would expect that dropdown to be an actual dropdown though..."* ⇒ the real picker.
   *
   * ⛔ **EVERY PATH IS BUILT HERE, INCLUDING THE PICKER'S** — the rule this file has held since
   * The `SectionRail` module doc: the rail constructs no routes. ⚠️ `appScopedPath(id, '')` rather than a literal
   * `/apps/${id}`, so a picker row and a rail item can never disagree about what an app's root is.
   */
  const scopePicker: RailScopePicker | null =
    apps !== null && apps.length >= MIN_APPS_FOR_PICKER && scopedAppId !== null
      ? {
          choices: apps.map((a) => ({
            id: a.id,
            name: a.name,
            subject: a.slug,
            to: appScopedPath(a.id, ''),
          })),
          currentId: scopedAppId,
          allTo: '/apps',
          allLabel: 'All apps',
          searchable: true,
        }
      : null;

  /*
   * The list row of the current data plane, or `undefined` when the list holds no such row (D8). The
   * URL can write the id in any case and the server writes it in lower case, so the match ignores
   * case, as `resolveDetailScope` and `dataPlaneRoute` do.
   */
  const currentRecordId = dataPlaneScope === null ? '' : dataPlaneScope.recordId.toLowerCase();
  const currentDataPlane =
    dataPlanes === null ? undefined : dataPlanes.find((dp) => dp.id.toLowerCase() === currentRecordId);

  // The data plane picker, or `null` for a label (D1, D3).
  const dataPlanePicker: RailScopePicker | null =
    dataPlaneScope !== null && dataPlanes !== null && dataPlanes.length >= MIN_DATA_PLANES_FOR_PICKER
      ? buildDataPlanePicker(dataPlanes, currentDataPlane, dataPlaneScope, pathname)
      : null;

  /*
   * ⭐⭐ **ONE SCOPE DESCRIPTOR, TWO ADOPTERS — R9-13.**
   *
   * ⛔ **THE TEST THIS SHAPE HAD TO PASS is the lead's, and it is the right one:** *"if you can
   * imagine a tenant rail needing another `NavRail` edit, your generalisation is wrong."* So nothing
   * below is app-shaped or DP-shaped — `label` is a word, `title` is a name, `subject` is an optional
   * short handle, and the items and picker arrive already built. A tenant scope is a third arm HERE
   * and nothing anywhere else.
   *
   * ⚠️ **The two arms are mutually exclusive by PATH** (`/apps/…` vs `/platform/…`), so the ordering
   * is not a precedence rule that has to be got right — it is an `else`. It is written as a chain
   * rather than as two props precisely so that a future overlap is a visible decision instead of two
   * chips racing.
   *
   * ⛔ **`subject` IS OMITTED FOR THE DETAIL SCOPE, AND THAT IS THE DESIGN, NOT A GAP.** For an app
   * it is the slug — a short, human, URL-ish handle worth showing under the name. A data plane's
   * equivalent is a **36-character UUID** (measured: `11111111-1111-4111-8111-111111111111`), and
   * putting that in the chip's second line and again in the group label spends the rail's two
   * scarcest lines on a string nobody reads at a glance. The id is still on the page, in mono, in the
   * heading (`data-testid="dp-id"`) — which is where an operator copies it from.
   */
  const railScope: RailScope | null =
    scopedApp !== null
      ? {
          label: 'App',
          title: scopedApp.name,
          subject: scopedApp.slug,
          items: appScopedItems,
          picker: scopePicker,
        }
      : detailScope !== null
        ? {
            label: detailScope.scope.label,
            /*
             * The name from the list row comes before the raw id. So after a pick the chip names the
             * new data plane while its detail read is still in progress.
             */
            title: detailScopeName ?? currentDataPlane?.name ?? detailScope.recordId,
            items: detailScopedItems,
            // The picker exists so an operator can open the same tab on another data plane (D5).
            // The list holds only in-service data planes.
            picker: dataPlanePicker,
          }
        : null;

  const routed = (
    <Routes>
      <Route path="/" element={<Navigate to={landingPath(landing)} replace />} />
      {/*
        ⛔ ITERATING EVERY DECLARED ENTRY, NOT JUST THE VISIBLE ONES — this is the load-bearing
        part. If an invisible entry emitted no route the path would fall to `*` and silently
        redirect to the landing, which teaches the operator that the URL does not exist. The deck
        makes the opposite an explicit shell property: "Opening a screen you do not hold renders a
        403 panel… not a blank page and not a hidden link." R6 hand-wrote that once, for the
        catalog (`CatalogRoute`); it is now the generic rule, so every new screen inherits it
        instead of each re-deciding.

        ⚠️ `globalEntries` from the REGISTRY, not the module-level `GLOBAL_ENTRIES` import (lead
        ruling ACK-ws0-1 / G5). In production the two are the same array — the provider's prop
        defaults to it — but reading the registry is what lets a test declare an entry the shipped
        manifest does not have yet. With the module import, every route-table and detail-route
        assertion would be unwritable until the first real Platform screen landed, i.e. WS-0's
        frame would ship with no test of either.

        ⚠️ `visibleGlobal.includes(e)` is REFERENCE IDENTITY, and it is correct: `visibleGlobal` is
        `globalEntries.filter(...)`, so the elements ARE the same objects. ⛔ Do not "harden" it to
        an id comparison — with the test-override props an id match would consider a DIFFERENT
        entry with the same id visible, which is a fake-visibility bug reachable only in tests.
      */}
      {globalEntries.map((e) => (
        <Route
          key={e.id}
          path={e.routePrefix}
          element={
            visibleGlobal.includes(e) ? (
              <AppLoader entry={e} />
            ) : (
              <NotAuthorizedPanel requested={e.routePrefix} landingTo={landingPath(landing)} />
            )
          }
        />
      ))}
      {/*
        Detail routes (R7-DEC-4): `${routePrefix}/:${paramName}`, no rail entry of their own.

        ⚠️ `requested` on the 403 is the PARENT PREFIX, not the resolved URL. Rendering the
        `:tenantId` back would echo a caller-supplied path segment into the panel; it already
        renders as inert text, but there is no reason to widen the surface for a value that adds
        nothing — the operator needs to know WHICH SCREEN, not which row.
      */}
      {globalEntries.map((e) =>
        e.detail === undefined ? null : (
          <Route
            key={e.detail.id}
            /*
              ⭐⭐ **R9-14 — A SCOPED DETAIL GETS ONE SPLAT ROUTE *INSTEAD OF* THE PLAIN ONE, AND THE
              WORD `INSTEAD` IS THE ENTIRE CORRECTNESS ARGUMENT.**

              ⛔ Emitting BOTH `…/:dpId` and `…/:dpId/*` looks harmless and is the defect. React
              Router ranks the static-arity route above the splat, so `/platform/data-planes/x` would
              match the FIRST and `/platform/data-planes/x/logs` the SECOND — two DIFFERENT `<Route>`
              elements. React unmounts one and mounts the other, so **every navigation between
              Overview and any other destination remounts `DataPlanePane` and re-fires all six of its
              `useApiResource` reads** — the exact defect its header is structured to prevent
              (*"the operator would watch data they already had flick back to 'Loading…'"*). One route
              ⇒ the element never changes ⇒ React reconciles and the reads stand.

              ⚠️ **A splat matches the bare path too**, with `params['*'] === ''`. That is not an
              assumption: `/apps/:appId/*` has always served `/apps/A`, which is how `app-overview`'s
              `subPath: ''` is reachable at all. So the record's own URL keeps working and needs no
              second route.
            */
            path={
              e.detail.scoped === undefined
                ? `${e.routePrefix}/:${e.detail.paramName}`
                : `${e.routePrefix}/:${e.detail.paramName}/*`
            }
            element={
              visibleGlobal.includes(e) ? (
                <AppLoader entry={detailShellEntry(e, e.detail)} />
              ) : (
                <NotAuthorizedPanel requested={e.routePrefix} landingTo={landingPath(landing)} />
              )
            }
          />
        ),
      )}
      {/*
        CREATE routes (#1242): `${routePrefix}/${segment}`, no rail entry of their own — reached from
        the parent list's `PageHeading.primaryAction`.

        ⛔ EMITTED AFTER the detail routes, and the ORDER IS NOT WHAT MAKES IT WORK. react-router
        ranks a static segment above a dynamic one regardless of declaration order, so
        `/platform/tenants/new` cannot fall into `/platform/tenants/:tenantId` as `tenantId="new"`.
        ⚠️ That is a claim about the ROUTER, not about this file — `entry-create.test.tsx` asserts it
        with a create and a detail route declared together, because the day the ranking changes the
        symptom is a detail screen fetching an entity called "new".

        ⚠️ Same 403 arm and the same `requested={e.routePrefix}` as the detail route above: the
        operator needs to know WHICH SCREEN they lack, and echoing the create segment back adds
        nothing.
      */}
      {globalEntries.map((e) =>
        e.create === undefined ? null : (
          <Route
            key={e.create.id}
            path={`${e.routePrefix}/${e.create.segment}`}
            element={
              visibleGlobal.includes(e) ? (
                <AppLoader entry={createShellEntry(e, e.create)} />
              ) : (
                <NotAuthorizedPanel requested={e.routePrefix} landingTo={landingPath(landing)} />
              )
            }
          />
        ),
      )}
      <Route
        path="/apps/:appId/*"
        element={<AppScopeRoute scoped={scoped} appId={scopedAppId} landing={landing} />}
      />
      {/*
        The four old Cost paths (D12). Each is a literal path, so any other
        `/platform/billing/<segment>` falls to `*`. They sit outside the visibility branch: the
        redirect lands on `/platform/billing`, whose own route then checks `cp:billing:read`.
        Literal elements, because `nav-targets-resolve.test.ts` reads each `<Route path="…">` here.
      */}
      <Route path="/platform/billing/ledger" element={<LegacyCostRedirect segment="ledger" />} />
      <Route path="/platform/billing/reconciliation" element={<LegacyCostRedirect segment="reconciliation" />} />
      <Route path="/platform/billing/autoscale" element={<LegacyCostRedirect segment="autoscale" />} />
      <Route path="/platform/billing/rate-card" element={<LegacyCostRedirect segment="rate-card" />} />
      {/*
        The two old Releases paths (plan 2026-10-13-console-ward-releases, D1 and D2). Each lands on
        the tab of the page that took it over; the target route checks its own permission.
      */}
      <Route path="/releases/adoption" element={<Navigate to="/releases/versions?tab=data-planes" replace />} />
      <Route path="/releases/ring-board" element={<Navigate to="/releases/channels?tab=apps" replace />} />
      {/*
        The old Registration hosts page is the Self-registration tab of OAuth clients (plan
        2026-10-21-console-ward-oauth-clients, I2); the target route checks `cp:settings:read`.
      */}
      <Route path="/settings/dcr-hosts" element={<Navigate to="/settings/oauth-clients?tab=self-registration" replace />} />
      <Route path="*" element={<Navigate to={landingPath(landing)} replace />} />
    </Routes>
  );

  const layout = (
    <SectionLayout
      /*
       * ⛔ **THE RAIL IS ON, ON EVERY AUTHENTICATED FRAME — INCLUDING IN APP SCOPE.** It used to be
       * `showRail={strip === null}`, because the app-scope strip REPLACED it. That replacement *is* the
       * owner's complaint: *"5 CP destinations were previously reachable only by first leaving the app
       * you were looking at."* The scoped group is now a SIBLING region inside this same rail (N-5), so
       * the console never leaves the screen.
       *
       * ⚠️ `showRail` itself STAYS a prop, and it stays `false` on `AuthenticatedShell`'s non-populated
       * frames: an entry-less rail does not degrade to nothing, it degrades to a full-height bordered
       * empty column — the strongest "this is broken, or still loading" signal on the page.
       */
      railItems={railItems}
      /*
       * ⭐ **ONE PROP WHERE THERE WERE THREE** (`appScopedItems` / `railAppScope` / `scopePicker`).
       * Three parallel props that were only ever meaningful together is what let the app scope and
       * its items drift apart in the type system; a descriptor makes "a scope with no items" and
       * "items with no scope" unrepresentable rather than merely unlikely.
       */
      railScope={railScope}
      // §5.3 — only in the single-app landing case, so it EXPLAINS the missing catalog rather
      // than leaving its absence looking like an omission.
      showSingleAppNote={landing.kind === 'app'}
      grantsError={grantsError}
      session={session}
    >
      {routed}
    </SectionLayout>
  );

  // The provider wraps the whole layout, so the rail reads the same app as the pane. It wraps in
  // and out of app scope alike, so the tree keeps one shape and the rail keeps its state.
  return <AppScopeProvider app={scopedApp}>{layout}</AppScopeProvider>;
}

export interface AppScopeRouteProps {
  /** The three-state answer to "can the caller reach the app in the URL?". */
  readonly scoped: ScopedAppResolution;
  readonly appId: string | null;
  readonly landing: Landing;
}

/*
 * The app drill-in frame, split into its own chunk as every pane is. `AppPane` imports WARD, and no
 * shell module does, so a static import here would put WARD in the entry chunk.
 */
const LazyAppPane = lazy(() => import('../panes/AppPane'));

/*
 * One `lazy()` per tab entry id, for the process. `AppLoader` keeps its own cache for the same
 * reason: a fresh `lazy()` on each render suspends again and flashes the fallback.
 */
const lazyTabByEntryId = new Map<string, ComponentType>();

/*
 * The tab module of `entry`, with no frame and no Suspense of its own. It renders inside `AppPane`,
 * so a tab that loads suspends to the frame's boundary and the frame stays on screen.
 */
function AppTab({ entry }: { readonly entry: AppScopedEntry }): React.JSX.Element {
  let Tab = lazyTabByEntryId.get(entry.id);
  if (Tab === undefined) {
    Tab = lazy(entry.loader);
    lazyTabByEntryId.set(entry.id, Tab);
  }
  return <Tab />;
}

/**
 * The app page of an id that the app list cannot decide. `AppScopeRoute` renders it for every
 * `unknown` resolution, so one instance stays mounted while a retry runs.
 *
 * - A failed read (`appsState.error`, or `appsState.rereadError` beside a kept list): the card
 *   `app-list-unknown`, which names the failed read with the server sentence, and the retry
 *   `app-list-unknown-reload`, which calls `AppsState.reload`.
 * - A retry pressed here, while it runs: the same card with the same button, marked
 *   `aria-disabled`, and the status `app-list-unknown-pending`. The button keeps keyboard focus,
 *   and a press while the retry runs starts no second read (R-015 item 4).
 * - Else (the list in flight, or no app list state in this tree): the spinner `app-scope-pending`.
 */
function AppListUndecided({ appsState }: { readonly appsState: AppsState | null }): React.JSX.Element {
  /* The sentence of the failure that the running retry follows, set when the retry is pressed here. */
  const [retryOf, setRetryOf] = useState<string | null>(null);
  const failure = appsState === null ? null : (appsState.error ?? appsState.rereadError);
  const retrying = appsState !== null && appsState.rereadPending && retryOf !== null;
  const message = failure?.message ?? (retrying ? retryOf : null);
  if (appsState === null || message === null) {
    return (
      <div data-testid="app-scope-pending" className="flex flex-1 items-center justify-center p-8">
        {/* `motion-reduce:animate-none` so a reduced-motion preference is honoured even on a
            transient indicator — the house pattern here (the `AppLoader` fallback). */}
        <Loader2 className="h-6 w-6 animate-spin text-muted motion-reduce:animate-none" aria-hidden />
        <span className="sr-only">Loading your apps</span>
      </div>
    );
  }
  const reload = appsState.reload;
  const retry = (): void => {
    if (retrying) return;
    setRetryOf(message);
    reload();
  };
  return (
    <div className="flex h-full items-center justify-center p-8">
      <div
        data-testid="app-list-unknown"
        role="status"
        className="flex max-w-lg flex-col items-center gap-3 rounded-xl border border-border-subtle bg-surface p-10 text-center"
      >
        <h1 className="text-sm font-medium text-primary">The console could not read your apps</h1>
        <p className="max-w-[52ch] break-words text-xs text-muted">{message}</p>
        {retrying && (
          <p data-testid="app-list-unknown-pending" className="text-xs text-muted">
            Reading your apps again…
          </p>
        )}
        <Button data-testid="app-list-unknown-reload" aria-disabled={retrying} onClick={retry}>
          Read your apps again
        </Button>
      </div>
    </div>
  );
}

/** The card that `AppTabsMissing` showed when the operator pressed its retry. */
type TabsRetryOrigin =
  /* `retryable` is `false` for a truncated list: no transient failure, so no retry is offered. */
  | { readonly kind: 'access-unknown'; readonly message: string; readonly retryable: boolean }
  | { readonly kind: 'tabs-missing' };

/**
 * The app root of an app in the caller's list, when no visible tab serves it.
 *
 * The first arm that holds decides:
 *
 * 1. A grants re-read in flight that no press here started: a spinner with a status
 *    (`app-access-pending`). The re-read after `POST /v1/apps` brings the owner grant that opens
 *    the new app's tabs.
 * 2. A failed grants read: the card `app-access-unknown`, which names the failed read with its
 *    sentence. The failure is the failure of the last re-read beside the kept grants
 *    (`reread.error`), or the unusable grants state (`grants.error` with no list). A `fetch`
 *    failure offers the retry `app-access-unknown-reload`; a truncated list offers none.
 * 3. An unusable grants state with no failure (the first read in flight): the spinner of arm 1.
 * 4. Else: a status that names the gap (`app-tabs-missing`), with the retry
 *    `app-tabs-missing-reload` when this tree can start a re-read.
 *
 * A retry pressed on either card keeps that card, with its button marked `aria-disabled` and the
 * status `app-access-retry-pending`, until the re-read lands. The button keeps keyboard focus,
 * and a press while the re-read runs starts no second read (R-015 item 4).
 */
function AppTabsMissing({
  reread,
  grants,
}: {
  readonly reread: GrantsReread | null;
  readonly grants: GrantsState | null;
}): React.JSX.Element {
  const [retryFrom, setRetryFrom] = useState<TabsRetryOrigin | null>(null);
  const unusable = grants !== null && grants.grants === null;
  const pending = reread?.pending === true;
  const retrying = pending && retryFrom !== null;
  const failure = reread?.error ?? (unusable ? grants.error : null);
  /* Starts the re-read from the card `origin`; a press while a retry from here runs does nothing. */
  function retry(origin: TabsRetryOrigin, reload: () => void): void {
    if (retrying) return;
    setRetryFrom(origin);
    reload();
  }
  const retryStatus = retrying ? (
    <p data-testid="app-access-retry-pending" className="text-xs text-muted">
      Reading your access again…
    </p>
  ) : null;
  const shown: TabsRetryOrigin | null = retrying
    ? retryFrom
    : pending
      ? null
      : failure !== null
        ? { kind: 'access-unknown', message: failure.message, retryable: failure.kind === 'fetch' }
        : unusable
          ? null
          : { kind: 'tabs-missing' };
  if (shown === null) {
    return (
      <div data-testid="app-access-pending" className="flex flex-1 items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted motion-reduce:animate-none" aria-hidden />
        <span role="status" className="sr-only">
          Reading your access to this app
        </span>
      </div>
    );
  }
  if (shown.kind === 'access-unknown') {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <div
          data-testid="app-access-unknown"
          role="status"
          className="flex max-w-lg flex-col items-center gap-3 rounded-xl border border-border-subtle bg-surface p-10 text-center"
        >
          <h1 className="text-sm font-medium text-primary">The console could not read your access to this app</h1>
          <p className="max-w-[52ch] break-words text-xs text-muted">{shown.message}</p>
          {retryStatus}
          {reread !== null && shown.retryable && (
            <Button data-testid="app-access-unknown-reload" aria-disabled={retrying} onClick={() => retry(shown, reread.reload)}>
              Read your access again
            </Button>
          )}
        </div>
      </div>
    );
  }
  return (
    <div className="flex h-full items-center justify-center p-8">
      <div
        data-testid="app-tabs-missing"
        role="status"
        className="flex max-w-lg flex-col items-center gap-3 rounded-xl border border-border-subtle bg-surface p-10 text-center"
      >
        <h1 className="text-sm font-medium text-primary">No tab of this app is open to you yet</h1>
        <p className="max-w-[52ch] text-xs text-muted">
          This app is in your list, but the access the console holds for you opens none of its tabs. A grant
          made a moment ago can be missing until the console reads your access again.
        </p>
        {retryStatus}
        {reread !== null && (
          <Button data-testid="app-tabs-missing-reload" aria-disabled={retrying} onClick={() => retry(shown, reread.reload)}>
            Read your access again
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * §7.2 — everything under `/apps/:appId`.
 *
 * - The list is unknown (not arrived, a failed read, or an absent id beside a kept list while a
 *   re-read is in flight or failed): `AppListUndecided`, a spinner (`app-scope-pending`) or a card
 *   with a retry (`app-list-unknown`), never a denial.
 * - The app is absent from a decided list: `NotAuthorizedPanel`, never a 404.
 * - A sub-path that no visible entry serves, with a usable grant list: a `replace` redirect to the
 *   app root.
 * - No visible entry serves the app root, or a deep link while the grant list is unusable:
 *   `AppTabsMissing` (`app-tabs-missing`), with a retry of the grants read.
 * - Else the frame (plan 2026-10-13-console-ward-app-drill-in, A-1): `AppPane` keyed on the app id,
 *   inside the page frame, with the tab module of the entry as its child. The route element stays the
 *   same across tab switches, so the frame and its reads stay mounted; a move to another app id
 *   mounts a new frame, so no state of the first app survives (R-003). Until the frame chunk loads,
 *   an unframed spinner (`app-frame-loading`) holds the region, as `AppLoader`'s fallback does.
 */
export function AppScopeRoute({ scoped, appId, landing }: AppScopeRouteProps): React.JSX.Element {
  const { visibleAppScoped } = useAppRegistry();
  const reread = useGrantsReread();
  const grants = useOptionalGrantsState();
  const appsState = useOptionalAppsContext();
  const subMatch = useMatch('/apps/:appId/*');
  // `params['*']` is the remainder after `/apps/:appId/`. Absent ⇒ the app root.
  const remainder = subMatch?.params['*'] ?? '';

  /*
   * ⛔ UNKNOWN IS NOT A DENIAL, and this arm is why the resolution is discriminated. Platform no
   * longer waits on `GET /v1/apps` (§2.2), so this component can render before the list arrives.
   * Answering `NotAuthorizedPanel` here would show a 403 that flickers into the right screen a
   * moment later — the worst possible shape, because the operator has already read it.
   */
  if (scoped.kind === 'unknown') {
    /* A failed read leaves the list unknown for good, so the page names it and reads it again. */
    return <AppListUndecided appsState={appsState} />;
  }

  /*
   * ⛔ NOT FOUND ⇒ NOT AUTHORIZED, and NOT a 404. The list is server-filtered by `cp:app:read`,
   * so "absent from your list" and "does not exist" are INDISTINGUISHABLE from the client — and
   * answering "not found" for an app that exists but is not yours is the information disclosure
   * the 404-vs-403 question exists for. "Not authorized" is true in both cases from this vantage
   * point.
   */
  if (scoped.kind === 'not-yours') {
    return (
      <NotAuthorizedPanel
        requested={appId === null ? '/apps' : `/apps/${appId}`}
        landingTo={landingPath(landing)}
      />
    );
  }

  const entry: AppScopedEntry | undefined = visibleAppScoped.find((e) => e.subPath === remainder);
  // With no usable grant list, a hidden tab says nothing about access, so a deep link waits here.
  const grantsUnusable = grants !== null && grants.grants === null;
  if (entry === undefined && remainder !== '' && !grantsUnusable) {
    // An unknown sub-path inside an app the caller CAN see is a stale or mistyped link, not a
    // denial — so it goes to the app's own root rather than to a 403.
    return <Navigate to={`/apps/${scoped.app.id}`} replace />;
  }
  if (entry === undefined) {
    // The app root has no visible tab, or a deep link waits on an unusable grant list. A redirect
    // here would name this same path. The grants re-read after a create is the usual cause.
    return <AppTabsMissing reread={reread} grants={grants} />;
  }
  return (
    <Suspense
      fallback={
        <div data-testid="app-frame-loading" className="flex flex-1 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted motion-reduce:animate-none" aria-hidden />
          <span className="sr-only">Loading</span>
        </div>
      }
    >
      <PaneFrame>
        <LazyAppPane key={scoped.app.id}>
          <AppTab entry={entry} />
        </LazyAppPane>
      </PaneFrame>
    </Suspense>
  );
}
